import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { TASK_STATUS_STYLES } from "../lib/constants";
import { toast } from "sonner";
import { ArrowLeft, LayoutGrid, List } from "lucide-react";

const COLS = ["Not Started", "In Progress", "Blocked", "Ready for Review", "Completed"];

export default function TaskBoard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [ws, setWs] = useState(null);
  const [view, setView] = useState("board");
  const [dragId, setDragId] = useState(null);

  const load = () => {
    api.get(`/tasks?workstream_id=${id}`).then((r) => setTasks(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get(`/workstreams/${id}`).then((r) => setWs(r.data.workstream)).catch(() => {});
  };
  useEffect(load, [id]);
  const uname = (uid) => users.find((u) => u.id === uid)?.name || "Unassigned";

  const move = async (taskId, status) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || t.status === status) return;
    let blocked_reason;
    if (status === "Blocked") { blocked_reason = prompt("What is blocking this task? (required)"); if (!blocked_reason) return; }
    setTasks((ts) => ts.map((x) => (x.id === taskId ? { ...x, status } : x)));
    try {
      await api.patch(`/tasks/${taskId}`, { status, blocked_reason, percent_complete: status === "Completed" ? 100 : t.percent_complete });
      toast.success("Task moved"); load();
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => nav(`/workstream/${id}`)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A687C] hover:text-[#1F2733]"><ArrowLeft className="h-4 w-4" /> Back to workstream</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="text-xs font-bold text-[#BA0C2F] uppercase tracking-wider">{ws?.code}</div><h1 className="text-2xl font-extrabold text-[#1F2733]">Task Board — {ws?.title}</h1></div>
        <div className="flex gap-1.5 bg-[#F4F6FA] p-1.5 rounded-full border border-[#DCE2EA]">
          <button data-testid="view-board" onClick={() => setView("board")} className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "board" ? "bg-[#BA0C2F] text-white font-semibold" : "text-[#5A687C]"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
          <button data-testid="view-list" onClick={() => setView("list")} className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#BA0C2F] text-white font-semibold" : "text-[#5A687C]"}`}><List className="h-4 w-4" /> List</button>
        </div>
      </div>

      {view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="kanban-board">
          {COLS.map((col) => (
            <div key={col} data-testid={`col-${col.toLowerCase().replace(/\s+/g, "-")}`}
              onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { move(dragId, col); setDragId(null); } }}
              className="bg-[#F4F6FA] rounded-2xl border border-[#DCE2EA] p-2 min-h-[140px]">
              {(() => { const n = tasks.filter((t) => t.status === col).length; const over = ["In Progress", "Ready for Review", "Blocked"].includes(col) && n > 4; return (
              <div className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${over ? "bg-[#FFF8EC]" : ""}`} data-testid={`wip-${col.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className={`text-xs font-bold ${over ? "text-[#824D0E]" : "text-[#1F2733]"}`}>{col}{over ? " ⚠ WIP" : ""}</span>
                <span className="text-[10px] font-bold text-[#98A2AF]">{n}</span>
              </div>
              ); })()}
              <div className="space-y-2">
                {tasks.filter((t) => t.status === col).map((t) => (
                  <div key={t.id} data-testid={`card-${t.id}`} draggable onDragStart={() => setDragId(t.id)}
                    className="bg-white rounded-xl border border-[#DCE2EA] p-3 cursor-grab active:cursor-grabbing shadow-sm hover:border-[#BA0C2F] transition-colors">
                    <div className="text-sm text-[#1F2733]">{t.title}</div>
                    <div className="text-[11px] text-[#98A2AF] mt-1">{uname(t.assignee_id)}</div>
                    {t.status === "Blocked" && t.blocked_reason && <div className="text-[11px] text-[#8A0822] mt-1 bg-[#FDF2F4] rounded px-1.5 py-0.5">⚠ {t.blocked_reason}</div>}
                    <div className="h-1 bg-[#F1F3F6] rounded-full mt-2 overflow-hidden"><div className="h-full bg-[#BA0C2F]" style={{ width: `${t.percent_complete}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] overflow-hidden overflow-x-auto" data-testid="task-list">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="bg-[#F4F6FA] text-[#5A687C] text-xs font-bold uppercase tracking-wider"><th className="text-left px-4 py-3">Task</th><th className="text-left px-4 py-3">Owner</th><th className="text-left px-4 py-3">Dates</th><th className="text-left px-4 py-3">Status</th></tr></thead>
            <tbody>{tasks.map((t) => (<tr key={t.id} className="border-b border-[#DCE2EA]/60 hover:bg-[#F4F6FA]/60"><td className="px-4 py-3">{t.title}</td><td className="px-4 py-3 text-[#5A687C]">{uname(t.assignee_id)}</td><td className="px-4 py-3 text-[#98A2AF] text-xs">{t.start_date} → {t.end_date}</td><td className="px-4 py-3"><span className={`text-xs font-semibold px-3 py-1 rounded-full border ${TASK_STATUS_STYLES[t.status] || ""}`}>{t.status}</span></td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
